import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from './syncSupabase';

const Q = {
  azul:"#1C3FAA", azulVivo:"#2A7FFF", azulMedio:"#6B6FD6",
  grisClaro:"#E8E9F6", grisPalido:"#F4F6FB", grisOscuro:"#2B2F36",
  grisPlata:"#8E93A1", blanco:"#FFFFFF",
  verde:"#22c55e", amarillo:"#f59e0b", rojo:"#e63946",
};

const STORAGE_CAP = 'alqos_capabilidad';
const STORAGE_CAP_CONFIG = 'alqos_capabilidad_config';
const STORAGE_MAQUINAS = 'alqos_maquinas';

const PROCESOS = ["Impresión Offset","Pre Prensa","Troquelado","Laminado","Barnizado","Corte","Estuchado / Armado","Control de Calidad","Expedición","Otro"];

const storage = {
  get: (k, def) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch(e) { console.error(e); } },
};

// ── ESTADÍSTICA ──────────────────────────────────────────────
function media(arr) { return arr.reduce((a,b) => a+b, 0) / arr.length; }
function desviacion(arr) {
  const m = media(arr);
  return Math.sqrt(arr.reduce((a,b) => a + Math.pow(b-m, 2), 0) / (arr.length - 1));
}
function calcularIndices(datos, lsi, lss, nominal) {
  if (datos.length < 2) return null;
  const m = media(datos);
  const s = desviacion(datos)
  if (s === 0) return null;
  const cp  = (lss - lsi) / (6 * s);
  const cpu = (lss - m)   / (3 * s);
  const cpl = (m - lsi)   / (3 * s);
  const cpk = Math.min(cpu, cpl);
  // Pp/Ppk usan desviación total (misma fórmula con muestra completa)
  const pp  = cp;
  const ppk = cpk;
  const porcentajeFuera = datos.filter(d => d < lsi || d > lss).length / datos.length * 100;
  return { cp, cpk, cpu, cpl, pp, ppk, m, s, porcentajeFuera };
}
function semaforo(val) {
  if (val === null || isNaN(val)) return { color: Q.grisPlata, label: "—" };
  if (val >= 1.67) return { color: Q.verde,    label: "✅ Excelente" };
  if (val >= 1.33) return { color: Q.verde,    label: "✅ Capaz" };
  if (val >= 1.00) return { color: Q.amarillo, label: "🟡 Marginal" };
  return              { color: Q.rojo,     label: "🔴 No capaz" };
}
function calcularRR(mediciones) {
  // mediciones: [{operador, pieza, valor}]
  if (mediciones.length < 4) return null;
  const operadores = [...new Set(mediciones.map(m => m.operador))];
  const piezas     = [...new Set(mediciones.map(m => m.pieza))];
  const repeatVals = operadores.map(op => {
    const vals = mediciones.filter(m => m.operador === op).map(m => Number(m.valor));
    return vals.length > 1 ? desviacion(vals) : 0;
  });
  const ev = media(repeatVals); // Equipment Variation (repetibilidad)
  const opMeans = operadores.map(op => media(mediciones.filter(m => m.operador === op).map(m => Number(m.valor))));
  const av = opMeans.length > 1 ? desviacion(opMeans) : 0; // Appraiser Variation (reproducibilidad)
  const grr = Math.sqrt(ev*ev + av*av);
  const allVals = mediciones.map(m => Number(m.valor));
  const tv = desviacion(allVals);
  const pctGRR = tv > 0 ? (grr / tv) * 100 : 0;
  let clasificacion = "";
  if (pctGRR < 10) clasificacion = "✅ Sistema aceptable";
  else if (pctGRR < 30) clasificacion = "🟡 Marginal — evaluar";
  else clasificacion = "🔴 Sistema inaceptable";
  return { ev, av, grr, pctGRR, tv, clasificacion, operadores, piezas };
}

// ── HISTOGRAMA SVG ───────────────────────────────────────────
function Histograma({ datos, lsi, lss, oscuro }) {
  if (datos.length < 2) return <p style={{ fontSize:"12px", color:Q.grisPlata, textAlign:"center", padding:"1rem" }}>Necesitás al menos 2 datos</p>;
  const min = Math.min(...datos, lsi);
  const max = Math.max(...datos, lss);
  const bins = 8;
  const paso = (max - min) / bins;
  const buckets = Array(bins).fill(0);
  datos.forEach(d => {
    let i = Math.floor((d - min) / paso);
    if (i >= bins) i = bins - 1;
    buckets[i]++;
  });
  const maxCnt = Math.max(...buckets);
  const W = 340, H = 160, pad = 30;
  const bw = (W - pad*2) / bins;
  const lsiX = pad + ((lsi - min) / (max - min)) * (W - pad*2);
  const lssX = pad + ((lss - min) / (max - min)) * (W - pad*2);
  return (
    <svg viewBox={`0 0 ${W} ${H+20}`} style={{ width:"100%", maxWidth:"360px" }}>
      {buckets.map((cnt, i) => {
        const x = pad + i * bw;
        const bh = maxCnt > 0 ? (cnt / maxCnt) * (H - pad) : 0;
        const y = H - bh;
        const center = min + (i + 0.5) * paso;
        const fuera = center < lsi || center > lss;
        return <rect key={i} x={x+1} y={y} width={bw-2} height={bh} fill={fuera ? Q.rojo+"99" : Q.azulVivo+"cc"} rx="2"/>;
      })}
      <line x1={lsiX} y1={pad/2} x2={lsiX} y2={H} stroke={Q.rojo} strokeWidth="1.5" strokeDasharray="4 2"/>
      <line x1={lssX} y1={pad/2} x2={lssX} y2={H} stroke={Q.rojo} strokeWidth="1.5" strokeDasharray="4 2"/>
      <text x={lsiX} y={pad/2-2} fontSize="8" fill={Q.rojo} textAnchor="middle">LSI</text>
      <text x={lssX} y={pad/2-2} fontSize="8" fill={Q.rojo} textAnchor="middle">LSS</text>
      <line x1={pad} y1={H} x2={W-pad} y2={H} stroke={oscuro?"#334":" #ccc"} strokeWidth="1"/>
      {[0,0.5,1].map(t => <text key={t} x={pad + t*(W-pad*2)} y={H+14} fontSize="8" fill={Q.grisPlata} textAnchor="middle">{(min + t*(max-min)).toFixed(2)}</text>)}
    </svg>
  );
}

// ── GRÁFICO X-R ──────────────────────────────────────────────
function GraficoXR({ datos, oscuro }) {
  if (datos.length < 4) return <p style={{ fontSize:"12px", color:Q.grisPlata, textAlign:"center", padding:"1rem" }}>Necesitás al menos 4 datos para X-R</p>;
  const grupos = [];
  for (let i = 0; i < datos.length - 1; i += 2) grupos.push([datos[i], datos[i+1]]);
  const xbar = grupos.map(g => media(g));
  const rbar = grupos.map(g => Math.max(...g) - Math.min(...g));
  const xbarMean = media(xbar);
  const rMean    = media(rbar);
  const D4 = 3.267, A2 = 1.880; // n=2
  const UCLx = xbarMean + A2 * rMean;
  const LCLx = xbarMean - A2 * rMean;
  const UCLR = D4 * rMean;
  const W = 340, H = 100, pad = 30;
  const renderLine = (arr, ucl, lcl, mean, color) => {
    const allVals = [...arr, ucl, lcl];
    const mn = Math.min(...allVals), mx = Math.max(...allVals);
    const scaleY = v => H - pad - ((v - mn) / (mx - mn + 0.0001)) * (H - pad*1.5);
    const scaleX = i => pad + (i / (arr.length - 1)) * (W - pad*2);
    const pts = arr.map((v,i) => `${scaleX(i)},${scaleY(v)}`).join(" ");
    return (
      <>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"/>
        {arr.map((v,i) => <circle key={i} cx={scaleX(i)} cy={scaleY(v)} r="3" fill={v > ucl || v < lcl ? Q.rojo : color}/>)}
        <line x1={pad} y1={scaleY(ucl)} x2={W-pad} y2={scaleY(ucl)} stroke={Q.rojo} strokeWidth="0.8" strokeDasharray="4 2"/>
        <line x1={pad} y1={scaleY(lcl)} x2={W-pad} y2={scaleY(lcl)} stroke={Q.rojo} strokeWidth="0.8" strokeDasharray="4 2"/>
        <line x1={pad} y1={scaleY(mean)} x2={W-pad} y2={scaleY(mean)} stroke={Q.verde} strokeWidth="0.8" strokeDasharray="4 2"/>
        <text x={W-pad+2} y={scaleY(ucl)+3} fontSize="7" fill={Q.rojo}>UCL</text>
        <text x={W-pad+2} y={scaleY(lcl)+3} fontSize="7" fill={Q.rojo}>LCL</text>
        <text x={W-pad+2} y={scaleY(mean)+3} fontSize="7" fill={Q.verde}>X̄</text>
      </>
    );
  };
  return (
    <div>
      <p style={{ fontSize:"11px", color:Q.grisPlata, marginBottom:"4px" }}>Gráfico X̄ (medias)</p>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%" }}>{renderLine(xbar, UCLx, LCLx, xbarMean, Q.azulVivo)}</svg>
      <p style={{ fontSize:"11px", color:Q.grisPlata, marginBottom:"4px", marginTop:"8px" }}>Gráfico R (rangos)</p>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%" }}>{renderLine(rbar, UCLR, 0, rMean, Q.azulMedio)}</svg>
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────────
export default function Capabilidad({ onVolver, oscuro, usuario }) {
  const [tab, setTab] = useState("estudios");

  // Config de características
  const [caracteristicas, setCaracteristicas] = useState(() => storage.get(STORAGE_CAP_CONFIG, [
    { id:1, nombre:"Registro de color", proceso:"Impresión Offset", unidad:"mm", lsi:0, lss:0.3, nominal:0.15, activa:true },
    { id:2, nombre:"Gramaje papel",     proceso:"Pre Prensa",       unidad:"g/m²", lsi:195, lss:205, nominal:200, activa:true },
  ]));
  // Catálogo de máquinas para vincular una característica a un equipo
  // puntual (indispensable cuando un proceso tiene varias máquinas en
  // paralelo, ej. las 8 troqueladoras -- sin esto, todas las mediciones
  // de "Troquelado" quedan mezcladas sin poder distinguir de qué máquina
  // vinieron). Mismo patrón de solo lectura ya probado en TPMMantenimiento.js.
  const [maquinas, setMaquinas] = useState([]);
  useEffect(() => {
    async function cargarMaquinas() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('maquinas').select('data');
        if (data?.length) setMaquinas(data.map(r => r.data));
        else setMaquinas(storage.get(STORAGE_MAQUINAS, []));
      } catch (e) { setMaquinas(storage.get(STORAGE_MAQUINAS, [])); }
    }
    cargarMaquinas();
  }, []);

  // Estudios
  const [estudios, setEstudios] = useState([]);


useEffect(() => {
  async function cargar() {
    try {
      const { supabase } = await import('./supabase');
      const { data } = await supabase.from('capabilidad').select('data');
      if (data && data.length > 0) setEstudios(data.map(r => r.data));
      else setEstudios(storage.get(STORAGE_CAP, []));
    } catch(e) { setEstudios(storage.get(STORAGE_CAP, [])); }
  }
  cargar();
}, []);
  const [estudioActivo, setEstudioActivo] = useState(null);

  // Forms
  const [formChar, setFormChar]       = useState({ proceso: PROCESOS[0] });
  const [formEstudio, setFormEstudio] = useState({});
  const [inputDatos, setInputDatos]   = useState("");
  const [formRR, setFormRR]           = useState({ operador:"", pieza:"", valor:"" });

  // Vistas
  const [vistaChar, setVistaChar]       = useState("listado");
  const [vistaEstudio, setVistaEstudio] = useState("listado");
  const [vistaRR, setVistaRR]           = useState("listado");

  const esAdmin = usuario?.rol === "Administrador";
  const bg      = oscuro ? "#09184D" : Q.grisPalido;
  const cardBg  = oscuro ? "#0d2160" : Q.blanco;
  const texto   = oscuro ? "#EDF2FA" : Q.grisOscuro;
  const borde   = oscuro ? "#1e3a8a" : Q.grisClaro;
  const navBg   = oscuro ? "#060f30" : Q.azul;
  const subtexto= oscuro ? "#9aabd4" : Q.grisPlata;
  const inp     = { width:"100%", padding:"9px 12px", borderRadius:"10px", border:`1.5px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto, fontFamily:"sans-serif", outline:"none" };
  const lbl     = { fontSize:"11px", color:subtexto, fontWeight:"700", display:"block", marginTop:"12px", letterSpacing:".05em", textTransform:"uppercase" };
  const card    = { background:cardBg, borderRadius:"14px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"14px" };

  // ── CARACTERÍSTICAS ──
  const agregarCaracteristica = () => {
    if (!formChar.nombre?.trim()) { alert("Nombre requerido."); return; }
    const nueva = { ...formChar, id: Date.now(), activa: true };
    const nuevas = [...caracteristicas, nueva];
    setCaracteristicas(nuevas);
    storage.set(STORAGE_CAP_CONFIG, nuevas); subirDatos(STORAGE_CAP_CONFIG);
    setFormChar({ proceso: PROCESOS[0] });
    setVistaChar("listado");
  };
  const toggleCaracteristica = (id) => {
    const nuevas = caracteristicas.map(c => c.id === id ? { ...c, activa: !c.activa } : c);
    setCaracteristicas(nuevas);
    storage.set(STORAGE_CAP_CONFIG, nuevas); subirDatos(STORAGE_CAP_CONFIG);
  };
  const eliminarCaracteristica = async (id) => {
    const nuevas = caracteristicas.filter(c => c.id !== id);
    setCaracteristicas(nuevas);
    storage.set(STORAGE_CAP_CONFIG, nuevas);
    await eliminarDato(STORAGE_CAP_CONFIG, id);
  };

  // ── ESTUDIOS ──
  const crearEstudio = () => {
    if (!formEstudio.caracteristicaId) { alert("Seleccioná una característica."); return; }
    const char = caracteristicas.find(c => c.id === Number(formEstudio.caracteristicaId));
    const nuevo = {
      id: Date.now(), ...formEstudio,
      caracteristica: char?.nombre, proceso: char?.proceso,
      maquinaId: char?.maquinaId || null, maquinaNombre: char?.maquinaNombre || null,
      lsi: char?.lsi, lss: char?.lss, nominal: char?.nominal, unidad: char?.unidad,
      datos: [], medicionesRR: [],
      fecha: new Date().toISOString(), creadoPor: usuario?.nombre,
    };
    const nuevos = [...estudios, nuevo];
    setEstudios(nuevos);
    storage.set(STORAGE_CAP, nuevos);
    subirDatos('alqos_capabilidad');
    setEstudioActivo(nuevo);
    setVistaEstudio("detalle");
    setFormEstudio({});
  };

  const agregarDatos = (estudioId) => {
    const vals = inputDatos.split(/[\n,;\s]+/).map(v => parseFloat(v)).filter(v => !isNaN(v));
    if (!vals.length) { alert("No hay valores válidos."); return; }
    const nuevos = estudios.map(e => e.id === estudioId ? { ...e, datos: [...e.datos, ...vals] } : e);
    setEstudios(nuevos);
    storage.set(STORAGE_CAP, nuevos);
    subirDatos('alqos_capabilidad');
    setEstudioActivo(nuevos.find(e => e.id === estudioId));
    setInputDatos("");
  };

  const limpiarDatos = (estudioId) => {
    const nuevos = estudios.map(e => e.id === estudioId ? { ...e, datos: [] } : e);
    setEstudios(nuevos);
    storage.set(STORAGE_CAP, nuevos);
    setEstudioActivo(nuevos.find(e => e.id === estudioId));
  };

  const eliminarEstudio = async (id) => {
    const nuevos = estudios.filter(e => e.id !== id);
    setEstudios(nuevos);
    storage.set(STORAGE_CAP, nuevos);
    await eliminarDato('alqos_capabilidad', id);
    setEstudioActivo(null);
    setVistaEstudio("listado");
  };

  // ── R&R ──
  const agregarMedicionRR = (estudioId) => {
    if (!formRR.operador?.trim() || !formRR.pieza?.trim() || !formRR.valor) { alert("Completá todos los campos."); return; }
    const nueva = { ...formRR, id: Date.now() };
    const nuevos = estudios.map(e => e.id === estudioId ? { ...e, medicionesRR: [...(e.medicionesRR||[]), nueva] } : e);
    setEstudios(nuevos);
    storage.set(STORAGE_CAP, nuevos);
    setEstudioActivo(nuevos.find(e => e.id === estudioId));
    setFormRR({ operador: formRR.operador, pieza:"", valor:"" });
  };

  const eliminarMedicionRR = (estudioId, medId) => {
    const nuevos = estudios.map(e => e.id === estudioId ? { ...e, medicionesRR: e.medicionesRR.filter(m => m.id !== medId) } : e);
    setEstudios(nuevos);
    storage.set(STORAGE_CAP, nuevos);
    setEstudioActivo(nuevos.find(e => e.id === estudioId));
  };

  // ── RENDER ──
  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
      {/* NAVBAR */}
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100, boxShadow:`0 2px 12px ${Q.azul}40` }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={onVolver} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="capabilidad" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="capabilidad" oscuro={oscuro} />}
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>📊 Capabilidad y MSA</span>
        </div>
        <CampanaNotificaciones oscuro={oscuro} modulo="Capabilidad" />
      </div>

      <div style={{ padding:"1.2rem 1.5rem", maxWidth:"860px", margin:"0 auto" }}>
        {/* TABS */}
        <div style={{ display:"flex", gap:"6px", marginBottom:"1.2rem", background:cardBg, borderRadius:"12px", padding:"6px", border:`1px solid ${borde}` }}>
          {[["estudios","📊 Estudios Cp/Cpk"],["rr","🔬 MSA / R&R"],["config","⚙️ Características"]].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ flex:1, padding:"9px", borderRadius:"8px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"700", background:tab===id?`linear-gradient(135deg,${Q.azulVivo},${Q.azul})`:"transparent", color:tab===id?"white":subtexto }}>
              {label}
            </button>
          ))}
        </div>

        {/* ══ TAB ESTUDIOS ══════════════════════════════════════ */}
        {tab === "estudios" && (
          <div>
            {vistaEstudio === "nuevo" ? (
              <div style={card}>
                <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.azulVivo, marginBottom:"16px" }}>+ Nuevo estudio de capabilidad</h3>
                <span style={lbl}>Característica a estudiar</span>
                <select onChange={e => setFormEstudio(f => ({...f, caracteristicaId: e.target.value}))} style={inp}>
                  <option value="">— Seleccionar —</option>
                  {caracteristicas.filter(c => c.activa).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.proceso}{c.maquinaNombre ? ` — ${c.maquinaNombre}` : ""})</option>)}
                </select>
                <span style={lbl}>Descripción del estudio</span>
                <input onChange={e => setFormEstudio(f => ({...f, descripcion: e.target.value}))} style={inp} placeholder="Ej: Estudio inicial proceso impresión"/>
                <span style={lbl}>Operador / Responsable</span>
                <input onChange={e => setFormEstudio(f => ({...f, operador: e.target.value}))} style={inp}/>
                <span style={lbl}>Instrumento de medición</span>
                <input onChange={e => setFormEstudio(f => ({...f, instrumento: e.target.value}))} style={inp} placeholder="Ej: Calibre digital Mitutoyo"/>
                <div style={{ display:"flex", gap:"10px", marginTop:"1rem" }}>
                  <button onClick={() => setVistaEstudio("listado")} style={{ flex:1, padding:"11px", background:"none", border:`1px solid ${borde}`, borderRadius:"10px", cursor:"pointer", color:texto }}>Cancelar</button>
                  <button onClick={crearEstudio} style={{ flex:2, padding:"11px", background:`linear-gradient(135deg,${Q.azulVivo},${Q.azul})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"14px", fontWeight:"700" }}>Crear estudio</button>
                </div>
              </div>
            ) : vistaEstudio === "detalle" && estudioActivo ? (
              <div>
                <button onClick={() => { setVistaEstudio("listado"); setEstudioActivo(null); }} style={{ marginBottom:"1rem", padding:"6px 14px", background:"none", border:`1px solid ${borde}`, borderRadius:"8px", cursor:"pointer", color:texto, fontSize:"13px" }}>← Volver</button>

                {/* INFO */}
                <div style={{ ...card, background: oscuro?"#0a1f5c":"#eff6ff", border:`1px solid ${Q.azulVivo}30` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div>
                      <h3 style={{ fontSize:"15px", fontWeight:"700", color:Q.azulVivo, margin:"0 0 4px" }}>{estudioActivo.caracteristica}</h3>
                      <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>{estudioActivo.proceso}{estudioActivo.maquinaNombre ? ` · ⚙️ ${estudioActivo.maquinaNombre}` : ""} · {estudioActivo.instrumento || "—"} · {estudioActivo.creadoPor}</p>
                    </div>
                    {esAdmin && <button onClick={() => eliminarEstudio(estudioActivo.id)} style={{ background:"none", border:"none", color:Q.rojo, cursor:"pointer", fontSize:"13px" }}>🗑 Eliminar</button>}
                  </div>
                  <div style={{ display:"flex", gap:"12px", marginTop:"12px", flexWrap:"wrap" }}>
                    {[["LSI", estudioActivo.lsi, Q.rojo],["Nominal", estudioActivo.nominal, Q.azulVivo],["LSS", estudioActivo.lss, Q.rojo],["Unidad", estudioActivo.unidad, subtexto]].map(([k,v,c]) => (
                      <div key={k} style={{ textAlign:"center" }}>
                        <p style={{ fontSize:"10px", color:subtexto, margin:0, textTransform:"uppercase", letterSpacing:".05em" }}>{k}</p>
                        <p style={{ fontSize:"14px", fontWeight:"700", color:c, margin:0 }}>{v}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* INGRESAR DATOS */}
                <div style={card}>
                  <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"12px" }}>Ingresar mediciones</h3>
                  <p style={{ fontSize:"12px", color:subtexto, margin:"0 0 8px" }}>Pegá varios valores separados por coma, espacio o enter. Ej: 0.12, 0.15, 0.18</p>
                  <textarea rows={4} value={inputDatos} onChange={e => setInputDatos(e.target.value)}
                    style={{ ...inp, resize:"none", fontFamily:"monospace" }}
                    placeholder="0.12&#10;0.15&#10;0.18&#10;0.13"/>
                  <div style={{ display:"flex", gap:"8px", marginTop:"8px" }}>
                    <button onClick={() => agregarDatos(estudioActivo.id)}
                      style={{ flex:2, padding:"10px", background:Q.azulVivo, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"700" }}>
                      ➕ Agregar {inputDatos.split(/[\n,;\s]+/).filter(v => !isNaN(parseFloat(v))).length} datos
                    </button>
                    {estudioActivo.datos.length > 0 && (
                      <button onClick={() => limpiarDatos(estudioActivo.id)}
                        style={{ flex:1, padding:"10px", background:"none", border:`1px solid ${Q.rojo}`, borderRadius:"10px", cursor:"pointer", fontSize:"13px", color:Q.rojo }}>
                        🗑 Limpiar
                      </button>
                    )}
                  </div>
                  <p style={{ fontSize:"11px", color:subtexto, margin:"8px 0 0" }}>Datos ingresados: <strong style={{ color:texto }}>{estudioActivo.datos.length}</strong></p>
                </div>

                {/* ÍNDICES */}
                {estudioActivo.datos.length >= 2 && (() => {
                  const idx = calcularIndices(estudioActivo.datos, Number(estudioActivo.lsi), Number(estudioActivo.lss), Number(estudioActivo.nominal));
                  if (!idx) return null;
                  return (
                    <div>
                      {/* Semáforo principal */}
                      <div style={{ ...card, background: oscuro?"#0a1f0a":`${semaforo(idx.cpk).color}10`, border:`2px solid ${semaforo(idx.cpk).color}40`, textAlign:"center" }}>
                        <p style={{ fontSize:"11px", color:subtexto, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:".1em" }}>Índice Cpk</p>
                        <p style={{ fontSize:"48px", fontWeight:"800", color:semaforo(idx.cpk).color, margin:"0 0 4px", lineHeight:1 }}>{idx.cpk.toFixed(3)}</p>
                        <p style={{ fontSize:"14px", fontWeight:"700", color:semaforo(idx.cpk).color, margin:0 }}>{semaforo(idx.cpk).label}</p>
                      </div>

                      {/* Tabla de índices */}
                      <div style={card}>
                        <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"12px", textTransform:"uppercase", letterSpacing:".05em" }}>Índices de capabilidad</h3>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(130px,1fr))", gap:"8px" }}>
                          {[["Cp",idx.cp],["Cpk",idx.cpk],["CPU",idx.cpu],["CPL",idx.cpl],["Pp",idx.pp],["Ppk",idx.ppk]].map(([label,val]) => (
                            <div key={label} style={{ background: oscuro?"#091840":Q.grisPalido, borderRadius:"10px", padding:"10px", textAlign:"center", border:`1px solid ${semaforo(val).color}40` }}>
                              <p style={{ fontSize:"11px", color:subtexto, margin:"0 0 4px", fontWeight:"700" }}>{label}</p>
                              <p style={{ fontSize:"18px", fontWeight:"800", color:semaforo(val).color, margin:"0 0 2px" }}>{val.toFixed(3)}</p>
                              <p style={{ fontSize:"9px", color:semaforo(val).color, margin:0 }}>{semaforo(val).label}</p>
                            </div>
                          ))}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"8px", marginTop:"10px" }}>
                          {[["Media (X̄)", idx.m.toFixed(4)+" "+estudioActivo.unidad],["Desv. Est. (σ)", idx.s.toFixed(4)+" "+estudioActivo.unidad],["Fuera de spec", idx.porcentajeFuera.toFixed(2)+"%"]].map(([k,v]) => (
                            <div key={k} style={{ background: oscuro?"#091840":Q.grisPalido, borderRadius:"10px", padding:"10px", textAlign:"center" }}>
                              <p style={{ fontSize:"10px", color:subtexto, margin:"0 0 4px" }}>{k}</p>
                              <p style={{ fontSize:"13px", fontWeight:"700", color:texto, margin:0 }}>{v}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Histograma */}
                      <div style={card}>
                        <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"12px" }}>Distribución de datos</h3>
                        <div style={{ display:"flex", justifyContent:"center" }}>
                          <Histograma datos={estudioActivo.datos} lsi={Number(estudioActivo.lsi)} lss={Number(estudioActivo.lss)} oscuro={oscuro}/>
                        </div>
                        <div style={{ display:"flex", gap:"12px", justifyContent:"center", marginTop:"8px" }}>
                          <span style={{ fontSize:"11px", color:Q.azulVivo }}>■ Dentro de especificación</span>
                          <span style={{ fontSize:"11px", color:Q.rojo }}>■ Fuera de especificación</span>
                        </div>
                      </div>

                      {/* Gráfico X-R */}
                      <div style={card}>
                        <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"12px" }}>Carta de control X̄-R</h3>
                        <GraficoXR datos={estudioActivo.datos} oscuro={oscuro}/>
                      </div>

                      {/* Datos crudos */}
                      <div style={card}>
                        <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"10px" }}>Datos ({estudioActivo.datos.length})</h3>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                          {estudioActivo.datos.map((d, i) => (
                            <span key={i} style={{ fontSize:"12px", padding:"3px 8px", borderRadius:"6px", background: d < Number(estudioActivo.lsi) || d > Number(estudioActivo.lss) ? `${Q.rojo}22` : oscuro?"#091840":Q.grisPalido, color: d < Number(estudioActivo.lsi) || d > Number(estudioActivo.lss) ? Q.rojo : texto, border:`1px solid ${d < Number(estudioActivo.lsi) || d > Number(estudioActivo.lss) ? Q.rojo+"40" : borde}` }}>
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div>
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"1rem" }}>
                  <button onClick={() => setVistaEstudio("nuevo")}
                    style={{ padding:"7px 16px", background:Q.azulVivo, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"600" }}>
                    + Nuevo estudio
                  </button>
                </div>
                {estudios.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"4rem" }}>
                    <p style={{ fontSize:"40px" }}>📊</p>
                    <p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin estudios de capabilidad</p>
                    <p style={{ fontSize:"13px", color:subtexto }}>Creá un estudio y cargá mediciones para calcular Cp/Cpk</p>
                  </div>
                ) : estudios.map(e => {
                  const idx = e.datos.length >= 2 ? calcularIndices(e.datos, Number(e.lsi), Number(e.lss), Number(e.nominal)) : null;
                  return (
                    <div key={e.id} onClick={() => { setEstudioActivo(e); setVistaEstudio("detalle"); }}
                      style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1.5px solid ${idx ? semaforo(idx.cpk).color+"40" : borde}`, marginBottom:"10px", cursor:"pointer" }}
                      onMouseEnter={el => el.currentTarget.style.boxShadow=`0 4px 16px ${Q.azulVivo}20`}
                      onMouseLeave={el => el.currentTarget.style.boxShadow="none"}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div>
                          <p style={{ fontSize:"14px", fontWeight:"700", color:texto, margin:"0 0 4px" }}>{e.caracteristica}</p>
                          <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>{e.proceso}{e.maquinaNombre ? ` · ⚙️ ${e.maquinaNombre}` : ""} · {e.datos.length} mediciones · {e.creadoPor}</p>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          {idx ? (
                            <>
                              <p style={{ fontSize:"18px", fontWeight:"800", color:semaforo(idx.cpk).color, margin:0 }}>Cpk {idx.cpk.toFixed(3)}</p>
                              <p style={{ fontSize:"11px", color:semaforo(idx.cpk).color, margin:0 }}>{semaforo(idx.cpk).label}</p>
                            </>
                          ) : (
                            <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>Sin datos suficientes</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ TAB MSA / R&R ══════════════════════════════════════ */}
        {tab === "rr" && (
          <div>
            {vistaRR === "detalle" && estudioActivo ? (
              <div>
                <button onClick={() => { setVistaRR("listado"); }} style={{ marginBottom:"1rem", padding:"6px 14px", background:"none", border:`1px solid ${borde}`, borderRadius:"8px", cursor:"pointer", color:texto, fontSize:"13px" }}>← Volver</button>
                <div style={{ ...card, background: oscuro?"#0a1f5c":"#eff6ff", border:`1px solid ${Q.azulVivo}30` }}>
                  <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.azulVivo, margin:"0 0 4px" }}>MSA — {estudioActivo.caracteristica}</h3>
                  <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>Instrumento: {estudioActivo.instrumento || "—"}{estudioActivo.maquinaNombre ? ` · ⚙️ ${estudioActivo.maquinaNombre}` : ""}</p>
                </div>

                {/* Agregar medición */}
                <div style={card}>
                  <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"12px" }}>Agregar medición R&R</h3>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px" }}>
                    <div>
                      <span style={lbl}>Operador</span>
                      <input value={formRR.operador} onChange={e => setFormRR(f=>({...f,operador:e.target.value}))} style={inp} placeholder="Nombre"/>
                    </div>
                    <div>
                      <span style={lbl}>Pieza N°</span>
                      <input value={formRR.pieza} onChange={e => setFormRR(f=>({...f,pieza:e.target.value}))} style={inp} placeholder="1,2,3..."/>
                    </div>
                    <div>
                      <span style={lbl}>Valor medido</span>
                      <input type="number" value={formRR.valor} onChange={e => setFormRR(f=>({...f,valor:e.target.value}))} style={inp}/>
                    </div>
                  </div>
                  <button onClick={() => agregarMedicionRR(estudioActivo.id)}
                    style={{ marginTop:"10px", width:"100%", padding:"10px", background:Q.azulVivo, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"700" }}>
                    + Agregar medición
                  </button>
                </div>

                {/* Resultado R&R */}
                {(estudioActivo.medicionesRR||[]).length >= 4 && (() => {
                  const rr = calcularRR(estudioActivo.medicionesRR||[]);
                  if (!rr) return null;
                  const color = rr.pctGRR < 10 ? Q.verde : rr.pctGRR < 30 ? Q.amarillo : Q.rojo;
                  return (
                    <div style={{ ...card, border:`2px solid ${color}40` }}>
                      <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"14px" }}>Resultados MSA / Gauge R&R</h3>
                      <div style={{ textAlign:"center", marginBottom:"16px" }}>
                        <p style={{ fontSize:"11px", color:subtexto, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:".1em" }}>%GR&R</p>
                        <p style={{ fontSize:"42px", fontWeight:"800", color, margin:"0 0 4px", lineHeight:1 }}>{rr.pctGRR.toFixed(1)}%</p>
                        <p style={{ fontSize:"13px", fontWeight:"700", color, margin:0 }}>{rr.clasificacion}</p>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"8px" }}>
                        {[["EV (Repet.)", rr.ev.toFixed(4)],["AV (Reprod.)", rr.av.toFixed(4)],["GR&R", rr.grr.toFixed(4)],["Variación total", rr.tv.toFixed(4)],["Operadores", rr.operadores.length],["Piezas", rr.piezas.length]].map(([k,v]) => (
                          <div key={k} style={{ background: oscuro?"#091840":Q.grisPalido, borderRadius:"10px", padding:"10px", textAlign:"center" }}>
                            <p style={{ fontSize:"10px", color:subtexto, margin:"0 0 2px" }}>{k}</p>
                            <p style={{ fontSize:"14px", fontWeight:"700", color:texto, margin:0 }}>{v}</p>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop:"12px", padding:"10px", borderRadius:"10px", background: oscuro?"#091840":Q.grisPalido }}>
                        <p style={{ fontSize:"11px", color:subtexto, margin:"0 0 4px", fontWeight:"700" }}>Criterio de aceptación:</p>
                        <p style={{ fontSize:"11px", color:Q.verde, margin:"0 0 2px" }}>✅ %GR&R {"<"} 10% — Sistema de medición aceptable</p>
                        <p style={{ fontSize:"11px", color:Q.amarillo, margin:"0 0 2px" }}>🟡 %GR&R 10-30% — Marginal, evaluar según aplicación</p>
                        <p style={{ fontSize:"11px", color:Q.rojo, margin:0 }}>🔴 %GR&R {">"} 30% — Sistema inaceptable, requiere mejora</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Tabla de mediciones */}
                {(estudioActivo.medicionesRR||[]).length > 0 && (
                  <div style={card}>
                    <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"10px" }}>Mediciones ({estudioActivo.medicionesRR.length})</h3>
                    {estudioActivo.medicionesRR.map(m => (
                      <div key={m.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:`0.5px solid ${borde}`, fontSize:"12px" }}>
                        <span style={{ color:subtexto }}>Op: <strong style={{ color:texto }}>{m.operador}</strong></span>
                        <span style={{ color:subtexto }}>Pieza: <strong style={{ color:texto }}>{m.pieza}</strong></span>
                        <span style={{ color:Q.azulVivo, fontWeight:"700" }}>{m.valor} {estudioActivo.unidad}</span>
                        <button onClick={() => eliminarMedicionRR(estudioActivo.id, m.id)} style={{ background:"none", border:"none", color:Q.rojo, cursor:"pointer" }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p style={{ fontSize:"13px", color:subtexto, marginBottom:"1rem" }}>Seleccioná un estudio para realizar el análisis MSA / Gauge R&R</p>
                {estudios.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"3rem" }}>
                    <p style={{ fontSize:"40px" }}>🔬</p>
                    <p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Primero creá un estudio en la tab Estudios Cp/Cpk</p>
                  </div>
                ) : estudios.map(e => (
                  <div key={e.id} onClick={() => { setEstudioActivo(e); setVistaRR("detalle"); }}
                    style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1px solid ${borde}`, marginBottom:"10px", cursor:"pointer" }}
                    onMouseEnter={el => el.currentTarget.style.boxShadow=`0 4px 16px ${Q.azulVivo}20`}
                    onMouseLeave={el => el.currentTarget.style.boxShadow="none"}>
                    <p style={{ fontSize:"14px", fontWeight:"700", color:texto, margin:"0 0 4px" }}>{e.caracteristica}</p>
                    <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>{e.proceso}{e.maquinaNombre ? ` · ⚙️ ${e.maquinaNombre}` : ""} · {(e.medicionesRR||[]).length} mediciones R&R</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ TAB CONFIGURACIÓN ══════════════════════════════════ */}
        {tab === "config" && (
          <div>
            {vistaChar === "nuevo" ? (
              <div style={card}>
                <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.azulVivo, marginBottom:"16px" }}>+ Nueva característica</h3>
                <span style={lbl}>Nombre de la característica</span>
                <input onChange={e => setFormChar(f=>({...f,nombre:e.target.value}))} style={inp} placeholder="Ej: Registro de color"/>
                <span style={lbl}>Proceso</span>
                <select onChange={e => setFormChar(f=>({...f,proceso:e.target.value}))} style={inp}>
                  {PROCESOS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {maquinas.length > 0 && (
                  <>
                    <span style={lbl}>Vincular a máquina cargada (opcional — indispensable si el proceso tiene varios equipos en paralelo, ej. Troquelado)</span>
                    <select onChange={e => {
                      const id = e.target.value ? Number(e.target.value) : null;
                      const m = maquinas.find(x => x.id === id);
                      setFormChar(f => ({ ...f, maquinaId: id, maquinaNombre: m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : null }));
                    }} style={inp}>
                      <option value="">— Sin vincular (proceso genérico) —</option>
                      {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
                    </select>
                  </>
                )}
                <span style={lbl}>Unidad de medida</span>
                <input onChange={e => setFormChar(f=>({...f,unidad:e.target.value}))} style={inp} placeholder="mm, g/m², µm, etc."/>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px" }}>
                  <div><span style={lbl}>LSI (Límite inferior)</span><input type="number" step="any" onChange={e => setFormChar(f=>({...f,lsi:Number(e.target.value)}))} style={inp}/></div>
                  <div><span style={lbl}>Nominal</span><input type="number" step="any" onChange={e => setFormChar(f=>({...f,nominal:Number(e.target.value)}))} style={inp}/></div>
                  <div><span style={lbl}>LSS (Límite superior)</span><input type="number" step="any" onChange={e => setFormChar(f=>({...f,lss:Number(e.target.value)}))} style={inp}/></div>
                </div>
                <div style={{ display:"flex", gap:"10px", marginTop:"1rem" }}>
                  <button onClick={() => setVistaChar("listado")} style={{ flex:1, padding:"11px", background:"none", border:`1px solid ${borde}`, borderRadius:"10px", cursor:"pointer", color:texto }}>Cancelar</button>
                  <button onClick={agregarCaracteristica} style={{ flex:2, padding:"11px", background:`linear-gradient(135deg,${Q.azulVivo},${Q.azul})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"14px", fontWeight:"700" }}>Guardar</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"1rem" }}>
                  <button onClick={() => setVistaChar("nuevo")} style={{ padding:"7px 16px", background:Q.azulVivo, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"600" }}>+ Nueva característica</button>
                </div>
                {caracteristicas.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"3rem" }}>
                    <p style={{ fontSize:"40px" }}>⚙️</p>
                    <p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin características configuradas</p>
                  </div>
                ) : caracteristicas.map(c => (
                  <div key={c.id} style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1px solid ${borde}`, marginBottom:"10px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <p style={{ fontSize:"13px", fontWeight:"700", color:texto, margin:"0 0 4px" }}>{c.nombre}</p>
                        <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>{c.proceso}{c.maquinaNombre ? ` · ⚙️ ${c.maquinaNombre}` : ""} · LSI {c.lsi} | {c.nominal} | LSS {c.lss} {c.unidad}</p>
                      </div>
                      <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
                        <button onClick={() => toggleCaracteristica(c.id)}
                          style={{ padding:"4px 12px", borderRadius:"8px", border:`1px solid ${c.activa?Q.verde:Q.rojo}`, background:c.activa?`${Q.verde}15`:`${Q.rojo}15`, color:c.activa?Q.verde:Q.rojo, cursor:"pointer", fontSize:"11px", fontWeight:"700" }}>
                          {c.activa?"Activa":"Inactiva"}
                        </button>
                        {esAdmin && <button onClick={() => eliminarCaracteristica(c.id)} style={{ background:"none", border:"none", color:Q.rojo, cursor:"pointer", fontSize:"16px" }}>×</button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p style={{ fontSize:"11px", color:subtexto, textAlign:"center", marginTop:"1rem" }}>
          ALQOS™ by QORVEX · Capabilidad y MSA · ISO 9001:2015 §9.1
        </p>
      </div>
    </div>
  );
}
